import React, { useState, useEffect, useRef, use } from "react";
import { supabase } from "../../supabaseClient"; // Import Supabase client
import { saveDraft, getDraft, deleteDraft } from "../common/DraftHistory";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css"; // Import Bootstrap Icons
import { Modal, Button } from "react-bootstrap"; // Top pe import
import ReplyModal from "../ReplyModal";
import MentionsInput from "../common/MentionsInput";
import MeetingModal from "../MeetingChatModal.jsx";
import SessionPopup from "../common/SessionPopup";
import QueueSessionPopup from "../common/QueueSessionPopup";
import ConfirmationPopup from "../common/ConfirmationPopup"; // Import ConfirmationPopup
import useResetSession from "../common/ResetSession";
import { createChatSession } from "../../chatSession";
import { firstMessage } from "../../utils/chatMessage";
import Cookies from "js-cookie";
import moment from "moment-timezone";
import { useTimezone } from "../../Context/TimezoneContext";
import {
  storeQueueChat,
  isViewQueue,
  uploadAudioAndGetUrl,
  uploadFileAndGetUrl,
} from "../../chatHistory"; // Add uploadFileAndGetUrl
import MeetingCreatesessionmodal from "./MeetingCreatesessionmodal";
import ModeSelector, { getSelectedMode } from "../common/ModeSelector";
import { getContextLimitsForPayload } from "../../utils/contextLimits";
import AddToSessionModal from "../Addtosessionmodal"; // Import at top
import Record from "../Record"; // Add Record component import
import ImageviewModal from "../common/ImageviewModal"; // Import common file modal
import VoiceTranscriptPanel from "./VoiceTranscriptPanel"; // Import Voice Transcript Panel

// Helper function to get file icon based on file extension
const getFileIcon = (fileName, size = 24, color = "currentColor") => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  const iconStyles = { width: size, height: size, flexShrink: 0 };

  // PDF Icon - Red
  if (extension === "pdf") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H11V8H7V10ZM15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#E53935"/>
        <path d="M14 3V9H20L14 3Z" fill="#FFCDD2"/>
        <text x="12" y="19" fontSize="6" fontWeight="bold" fill="white" textAnchor="middle">PDF</text>
      </svg>
    );
  }

  // Excel/Spreadsheet Icon - Green
  if (["xlsx", "xls", "csv"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#43A047"/>
        <path d="M14 3V9H20L14 3Z" fill="#C8E6C9"/>
        <path d="M7 13H9V15H7V13ZM10 13H12V15H10V13ZM13 13H15V15H13V13ZM7 16H9V18H7V16ZM10 16H12V18H10V16ZM13 16H15V18H13V16Z" fill="white"/>
      </svg>
    );
  }

  // Word Document Icon - Blue
  if (["docx", "doc"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#1976D2"/>
        <path d="M14 3V9H20L14 3Z" fill="#BBDEFB"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H13V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Text File Icon - Gray
  if (extension === "txt") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#757575"/>
        <path d="M14 3V9H20L14 3Z" fill="#E0E0E0"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H12V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Default Document Icon
  return (
    <svg viewBox="0 0 24 24" fill={color} style={iconStyles}>
      <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2ZM19 20H5V4H14V9H19V20Z"/>
    </svg>
  );
};

// Helper function to get file type from file name
const getFileTypeFromName = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return "pdf";
  }
  if (["xlsx", "xls"].includes(extension)) {
    return "excel";
  }
  if (extension === "csv") {
    return "csv";
  }
  if (["docx", "doc"].includes(extension)) {
    return "word";
  }
  if (extension === "txt") {
    return "txt";
  }
  return "document";
};

const RightSidebar = ({
  clearNotifications,
  notifications,
  onNotificationUpdate,
  onQueueResponse,
  onNotificationResponse,
  queueRequests,
  setQueueRequests,
  queueRefreshKey,
  userId,
  queueModalData,
  setQueueModalData,
  onMeetingResponse, // Add onMeetingResponse prop
  IsSidebardNotificationVisible,
  // Voice chat props
  isVoiceActive,
  voiceConversationHistory,
  voiceSessionDuration,
  showVoiceTab,
  shouldSwitchToVoiceTab,
  onCloseVoiceTab,
  onVoiceTabSwitched,
}) => {
  // Get user timezone from context
  const { userTimezone } = useTimezone();

  // Helper to format meeting date as 'MM-DD-YYYY hh:mm AM/PM'
  const formatMeetingDate = (dateStr) => {
    if (!dateStr) return "";
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj)) return dateStr;
    const pad = (n) => (n < 10 ? "0" + n : n);
    let month = pad(dateObj.getMonth() + 1);
    let day = pad(dateObj.getDate());
    let year = dateObj.getFullYear();
    let hours = dateObj.getHours();
    let minutes = pad(dateObj.getMinutes());
    let ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${month}-${day}-${year} ${pad(hours)}:${minutes} ${ampm}`;
  };
  const [activeTab, setActiveTab] = useState(
    () => Cookies.get("rightSidebarActiveTab") || "notifications"
  ); // Add this line
  const [currentSession, setCurrentSession] = useState(() =>
    Cookies.get("chat_session_id")
  );
  const [nestedQueueTab, setNestedQueueTab] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [showQueueFormPopup, setShowQueueFormPopup] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false); // Add state for confirmation popup
  const [showQueueConfirmationPopup, setShowQueueConfirmationPopup] =
    useState(false); // Add state for confirmation popup
  const [showMeetingConfirmationPopup, setShowMeetingConfirmationPopup] =
    useState(false);
  const [notificationsData, setNotificationsData] = useState([]); // Add this line to define the state
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true); // Loading state for notifications
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [workflowUrl, setWorkflowUrl] = useState(""); // Add state for workflow URL
  const [alreadyReplied, setAlreadyReplied] = useState(false);
  const [isPlayingId, setPlayingId] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  // const [queueRequests, setQueueRequests] = useState([]);
  const [isLoadingQueue, setIsLoadingQueue] = useState(false); // Loading state for queue
  const resetSession = useResetSession(); // Initialize the useResetSession hook
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [selectedQueueRequest, setSelectedQueueRequest] = useState(null);
  const [queueView, setQueueView] = useState(null);
  const [queueSelectId, setQueueSelectId] = useState(null);
  const [isQueueStandalone, setIsQueueStandalone] = useState(false);
  const [queueViewTitle, setQueueViewTitle] = useState(null);
  const [selectedMode, setSelectedMode] = useState(() => getSelectedMode());
  const modalBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionsInputRef = useRef(null);
  const [queueSearch, setQueueSearch] = useState("");
  const [sideConvoList, setSideConvoList] = useState([]);
  const [apiToken, setApiToken] = useState([]);

  const notificationListRef = useRef(null);
  const queueListRef = useRef(null);
  const meetingListRef = useRef(null);

  const [nestedNotifTab, setNestedNotifTab] = useState("All");
  const [notifSearch, setNotifSearch] = useState("");

  const [showQueueDeletePopup, setShowQueueDeletePopup] = useState(false);
  const [deletingQueueId, setDeletingQueueId] = useState(null);

  const [meetingNestedTab, setMeetingNestedTab] = useState("All");
  const [meetingSearch, setMeetingSearch] = useState("");
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [meetings, setMeetings] = useState([]);

  // Pagination states
  const ITEMS_PER_PAGE = 10;
  const [notifPage, setNotifPage] = useState(1);
  const [queuePage, setQueuePage] = useState(1);
  const [meetingPage, setMeetingPage] = useState(1);

  // Reset pagination when filters change
  useEffect(() => { setNotifPage(1); }, [nestedNotifTab, notifSearch]);
  useEffect(() => { setQueuePage(1); }, [nestedQueueTab, queueSearch]);
  useEffect(() => { setMeetingPage(1); }, [meetingNestedTab, meetingSearch]);

  // Add state for meeting session modal
  const [showMeetingSessionModal, setShowMeetingSessionModal] = useState(false);
  const [meetingSessionMeetingId, setMeetingSessionMeetingId] = useState(null);

  const zoomey_api_url = process.env.REACT_APP_ZOOMEY_API_URL;

  // 1. Add state at the top
  const [showAddToSessionModal, setShowAddToSessionModal] = useState(false);
  const [addToChatText, setAddToChatText] = useState("");

  // Add audio-related states for Queue Modal
  const [audioRecording, setAudioRecording] = useState(false);
  const [audioInput, setAudioInput] = useState(null); // Audio input from Record component
  const [isAudioProcessing, setIsAudioProcessing] = useState(false); // Audio processing state
  const [isLoading, setIsLoading] = useState(false); // General loading state
  const [isSending, setIsSending] = useState(false); // Sending state
  const [isStreaming, setIsStreaming] = useState(false); // Streaming state
  const [isUserQueyLoading, setIsUserQueyLoading] = useState(false); // User query loading
  const [isDarkModeInternal] = useState(false); // Dark mode state

  const [copyToTextArea, setCopyToTextArea] = useState(false);
  const [cancelAudio, setCancelAudio] = useState(false);

  // Add file upload states for Queue Modal
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("image");
  const [selectedFileName, setSelectedFileName] = useState("");

  useEffect(() => {
    if (IsSidebardNotificationVisible) {
      // Reset scroll for all tabs
      if (notificationListRef.current)
        notificationListRef.current.scrollTop = 0;
      if (queueListRef.current) queueListRef.current.scrollTop = 0;
      if (meetingListRef.current) meetingListRef.current.scrollTop = 0;
    }
  }, [IsSidebardNotificationVisible]);

  // Show loader when switching to notifications tab
  useEffect(() => {
    if (activeTab === "notifications") {
      setIsLoadingNotifications(true);
      // Small delay to allow data to be processed, then hide loader
      const timer = setTimeout(() => {
        setIsLoadingNotifications(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  // Auto-switch to Voice tab when voice starts
  useEffect(() => {
    if (shouldSwitchToVoiceTab && showVoiceTab) {
      setActiveTab("Voice");
      onVoiceTabSwitched?.(); // Reset the trigger
    }
  }, [shouldSwitchToVoiceTab, showVoiceTab, onVoiceTabSwitched]);

  // Voice tab is always available now - no need to switch away

  //fetch Draft content for queue
  useEffect(() => {
    const fetchQueueDraft = async () => {
      if (showQueueModal && queueView && userId) {
        const key = `queue_${queueView}_${userId}`;
        const draft = await getDraft(key);
        setReplyMessage(draft || "");
        if (draft) setReplyMessage(draft);
        // console.log("Fetched draft:", draft);
      }
    };
    fetchQueueDraft();
  }, [showQueueModal, queueView, userId]);

  function formatMentionSpacing(text) {
    return text.replace(/(@\w+):\s*/g, "$1: ");
  }

  const formatResponseMessage = (text) => {
    if (!text) return "";
    // If text is already HTML (from backend formatToHTML), linkify any remaining plain URLs
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return text.replace(/((?:^|[^"'=/>]))(https?:\/\/[^\s<>\[\]"']+)/g,
        (match, prefix, url) => `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      );
    }
    let html = text;

    // Protect URLs from double-processing by replacing them with placeholders
    const urlPlaceholders = [];
    // First protect URLs inside markdown link syntax [text](url)
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, linkText, url) => {
      urlPlaceholders.push({ type: "markdown", text: linkText, url });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });
    // Then protect standalone URLs
    html = html.replace(/(https?:\/\/[^\s<>\[\]()]+)/g, (match) => {
      urlPlaceholders.push({ type: "plain", url: match });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });

    html = html
      .replace(/###\s*(.*?)(?:\n|$)/g, "<h5>$1</h5>")
      .replace(/##\s*(.*?)(?:\n|$)/g, "<h4>$1</h4>")
      .replace(/#\s*(.*?)(?:\n|$)/g, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>')
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
      );

    // Restore URLs as proper <a> tags (no double-processing possible)
    html = html.replace(/URLPLACEHOLDER(\d+)ENDURL/g, (match, index) => {
      const entry = urlPlaceholders[parseInt(index)];
      if (entry.type === "markdown") {
        return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.text}</a>`;
      }
      return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.url}</a>`;
    });

    html = html.replace(/\{(.*?)\}/g, "$1");
    return html;
  };

  const truncateMessage = (text, maxWords) => {
    const words = text.split(" ");
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "...";
    }
    return text;
  };

  const fetchSessions = async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (session) {
      // Fetch parent sessions (where parent_session is null)
      const { data: sessionList, error: parentError } = await supabase
        .from("chat_session")
        .select("*")
        .eq("type", "topic")
        .eq("user_auth", session.user.id)
        .order("updated_at", { ascending: false });

      if (parentError) {
        console.error("Error fetching parent sessions:", parentError);
      } else {
        setSideConvoList(sessionList);
      }
    }
  };

  const storeApiToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      console.warn("No session found. User may not be logged in.");
      return;
    }
    setApiToken(session.access_token);
  };

  useEffect(() => {
    fetchSessions();
    storeApiToken();
    Cookies.set("rightSidebarActiveTab", activeTab, { expires: 30 });
  }, [activeTab]);

  const getTimeZone = (created_at) => {
    return moment
      .utc(created_at)
      .tz(userTimezone)
      .format("MM-DD-YYYY hh:mm A");
  };

  const timeAgo = (createdAt) => {
    const now = moment().tz(userTimezone);
    const createdTime = moment.utc(createdAt).tz(userTimezone);
    // const createdTime = moment(createdAt);
    const duration = moment.duration(now.diff(createdTime));

    const days = Math.max(0, duration.asDays());
    const hours = Math.max(0, duration.asHours());
    const minutes = Math.max(0, duration.asMinutes());

    if (days >= 1) {
      return `${Math.floor(days)} days ago`;
    } else if (hours >= 1) {
      return `${Math.floor(hours)} hours ago`;
    } else if (minutes < 1) {
      return "Just now";
    } else {
      return `${Math.floor(minutes)} min ago`;
    }
  };

  const sendChatMessage = async (message) => {
    // setReplyMessage("")
    const formattedMessage = formatMentionSpacing(message);
    console.log("Sending message:", formattedMessage);
    if (formattedMessage.trim() !== "") {
      const dataGet = await storeQueueChat(queueView, formattedMessage);
      const payload = {
        user_id: userId,
        session_id: queueView.toString(),
        store_id: dataGet[0].id.toString(),
        user_query: formattedMessage,
        mode: selectedMode,
        customContextLimits: getContextLimitsForPayload(),
      };
      getPopupContent(queueView);
      setReplyMessage("");
      // Delete draft after sending
      if (queueView && userId) {
        const key = `queue_${queueView}_${userId}`;
        await deleteDraft(key);
      }
      await fetch(zoomey_api_url + "/single_query", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(payload),
      }).then(async (response) => {
        if (response.status === 200) {
          // setShowQueueModal(false)
          setReplyMessage("");
        } else {
          // alert("Something went wrong with your server.");
        }
      });
    }
  };

  // File upload functions for Queue Modal
  const handleFileSelect = async (e) => {
    const file = e.target.files[0]; // Get only the first file
    if (file) {
      setSelectedFiles([file]); // Replace existing files with new single file

      // Create preview for the single file
      let preview;
      if (file.type.startsWith("image/")) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) =>
            resolve({
              file,
              type: "image",
              url: e.target.result,
              name: file.name,
            });
          reader.readAsDataURL(file);
        });
      } else {
        preview = {
          file,
          type: getFileTypeFromName(file.name),
          url: null,
          name: file.name,
        };
      }

      setFilePreviews([preview]); // Replace existing previews with new single preview
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSendWithFiles = async () => {
    if (selectedFiles.length === 0 && replyMessage.trim() === "") return;

    try {
      setIsSending(true);

      const fileToSend = selectedFiles[0]; // Get the single file
      const messageToSend = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
      mentionsInputRef.current?.clearMention();

      if (fileToSend) {
        // Process the single file
        const fileUrl = await uploadFileAndGetUrl(
          queueView,
          userId,
          fileToSend
        );

        if (!fileUrl) {
          console.error("File upload failed!");
          // Clear UI immediately even on error
          setSelectedFiles([]);
          setFilePreviews([]);
          setReplyMessage("");
          return;
        }

        // Compose the message for chat history
        let userQueryToSend = messageToSend;
        if (!userQueryToSend) {
          if (fileToSend.type.startsWith("image/")) {
            userQueryToSend = `Shared image: ${fileToSend.name}`;
          } else {
            userQueryToSend = `Shared file: ${fileToSend.name}`;
          }
        }

        // Store file message in database (for UI only, do NOT send file here)
        const dataGet = await storeQueueChat(
          queueView,
          userQueryToSend,
          fileUrl
        );

        // Clear UI immediately after sending (before waiting for response)
        setSelectedFiles([]);
        setFilePreviews([]);
        setReplyMessage("");

        // Add Attachment_URL to user_query same as ChatBot format
        let userQueryWithAttachment = messageToSend;
        if (fileUrl) {
          userQueryWithAttachment = messageToSend
            ? messageToSend + `\n Attachment_URL: [${fileUrl}]`
            : `Attachment_URL: [${fileUrl}]`;
        }

        // Prepare payload for main API (no file object needed, Attachment_URL is in user_query)
        const payload = {
          user_id: userId,
          session_id: queueView.toString(),
          store_id: dataGet[0].id.toString(),
          user_query: userQueryWithAttachment,
          mode: selectedMode,
          customContextLimits: getContextLimitsForPayload(),
        };
        console.log("Payload to send:", payload);
        // Only send file in payload to main API, not to Supabase
        const response = await fetch(zoomey_api_url + "/single_query", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 200) {
          console.log("File message sent successfully");
        } else {
          console.error("File message API failed:", response.status);
        }
      } else if (messageToSend !== "") {
        // Send text message only
        // Do NOT include payload.file if no file is being sent
        await sendChatMessage(messageToSend);
        // Clear UI immediately after sending text
        setReplyMessage("");
      }

      // Delete draft after sending
      if (queueView && userId) {
        const key = `queue_${queueView}_${userId}`;
        await deleteDraft(key);
      }

      // Refresh conversation
      setTimeout(() => {
        getPopupContent(queueView);
      }, 1000);
    } catch (error) {
      console.error("Error sending files:", error);
      alert("Failed to upload files. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const openFileModal = (fileUrl, fileType = "image", fileName = "File") => {
    setSelectedFileUrl(fileUrl);
    setSelectedFileType(fileType);
    setSelectedFileName(fileName);
    setShowFileModal(true);
  };

  useEffect(() => {
    async function fetchSession() {
      const { data: subscription } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          if (session?.user) {
            const fetchProfile = async () => {
              const { data: profile } = await supabase
                .from("profiles")
                .select("name, workflow_url")
                .eq("id", session.user.id)
                .single();
              setWorkflowUrl(profile?.workflow_url); // Set workflow URL
            };
            fetchProfile();
          }
        }
      );
    }
    fetchSession();
  }, []);

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = modalBodyRef.current.scrollHeight;
    }
  }, [showQueueModal, onQueueResponse, replyMessage, onNotificationResponse]);

  useEffect(() => {
    if (selectedNotification != null) {
      setAlreadyReplied(
        selectedNotification?.user_query !== null ? true : false
      );
    }
  }, [selectedNotification]);
  useEffect(() => {

     console.log('RightSidebar - Received notifications prop:', notifications);
  console.log('RightSidebar - Current userId:', userId);

    const sortedNotifications = [...notifications].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    setNotificationsData(sortedNotifications);
    setIsLoadingNotifications(false); // Data loaded, stop showing loader
  }, [notifications, onNotificationResponse]);

  const baseNotifications = (notificationsData || []).filter(
    (notif) => !notif.queuerequestid
  );

  // Notifications ko tab aur search ke hisaab se filter karne wala function
  const getFilteredNotifications = (paginate = true) => {
    let filtered = baseNotifications;
    switch (nestedNotifTab) {
      case "Unread":
        filtered = filtered.filter((notif) => !notif.is_read);
        break;
      case "Read":
        filtered = filtered.filter((notif) => notif.is_read);
        break;
      default:
        break;
    }
    if (notifSearch.trim()) {
      const search = notifSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (notif) =>
          (notif.title && notif.title.toLowerCase().includes(search)) ||
          (notif.message && notif.message.toLowerCase().includes(search))
      );
    }
    if (!paginate) return filtered;
    const startIndex = (notifPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getNotifTotalPages = () => Math.ceil((getFilteredNotifications(false)?.length || 0) / ITEMS_PER_PAGE);
  const getNotifTotalCount = () => getFilteredNotifications(false)?.length || 0;

  // Fetch queue requests from Supabase
  // const fetchRequestQueue = async () => {
  //   // setIsLoadingQueue(true);
  //   try {
  //     // First, get all queue_request records for current user
  //     const { data: queueRequests, error: requestError } = await supabase
  //       .from("queue_request")
  //       .select("*")
  //       // .eq("user_id", userId)  // ❌ Problem yahan hai!
  //       .order("response_time", { ascending: false });

  //     if (queueRequests) {
  //       // Get all queue_request IDs
  //       const requestIds = queueRequests.map((request) => request.id);

  //       // Then fetch all related queue_chat records
  //       const { data: queueChats, error: chatError } = await supabase
  //         .from("queue_chat")
  //         .select("*")
  //         .in("queue_request_id", requestIds)
  //         .eq("user_id", userId) // ✅ Filter user here instead of in the first query
  //         .order("created_at", { ascending: true });

  //       // Now manually associate the chat records with their requests
  //       if (queueChats) {
  //         queueRequests.forEach((request) => {
  //           // Find all chats for this request
  //           const relatedChats = queueChats.filter(
  //             (chat) => chat.queue_request_id === request.id
  //           );

  //           // Take only the first one (which will be the most recent due to our ordering)
  //           request.queue_chat =
  //             relatedChats.length > 0 ? relatedChats[0] : null;
  //         });
  //       }
  //     }

  //     if (requestError) {
  //       console.error("Error fetching queue requests:", requestError);
  //       setQueueRequests([]);
  //     } else {
  //       setQueueRequests(queueRequests || []);
  //     }
  //   } catch (err) {
  //     console.error("Error in fetchRequestQueue:", err);
  //     setQueueRequests([]);
  //   } finally {
  //     setIsLoadingQueue(false);
  //   }
  // };
  const fetchRequestQueue = async () => {
    setIsLoadingQueue(true); // Start loading
    try {
      // Step 1: Get queue_requests without user filter (since user_id column has issues)
      const { data: queueRequests, error: requestError } = await supabase
        .from("queue_request")
        .select("*")
        // Remove this line: .eq("user_id", userId)
        .order("response_time", { ascending: false });

      if (queueRequests) {
        // Get all queue_request IDs
        const requestIds = queueRequests.map((request) => request.id);

        // Step 2: Filter by user_id in queue_chat table instead
        const { data: queueChats, error: chatError } = await supabase
          .from("queue_chat")
          .select("*")
          .in("queue_request_id", requestIds)
          .eq("user_id", userId) // ✅ Filter user here instead
          .order("created_at", { ascending: true });

        // Step 3: Only keep requests that have user's chat data
        if (queueChats) {
          // Filter out requests that don't belong to current user
          const userRequestIds = [
            ...new Set(queueChats.map((chat) => chat.queue_request_id)),
          ];

          const userQueueRequests = queueRequests.filter((request) =>
            userRequestIds.includes(request.id)
          );

          // Associate chat data with requests
          userQueueRequests.forEach((request) => {
            const relatedChats = queueChats.filter(
              (chat) => chat.queue_request_id === request.id
            );
            request.queue_chat =
              relatedChats.length > 0 ? relatedChats[0] : null;
          });

          setQueueRequests(userQueueRequests || []);
        }
      }

      if (requestError) {
        console.error("Error fetching queue requests:", requestError);
        setQueueRequests([]);
      }
    } catch (err) {
      console.error("Error in fetchRequestQueue:", err);
      setQueueRequests([]);
    } finally {
      setIsLoadingQueue(false);
    }
  };

  // Fetch queue data when Queue tab is selected
  useEffect(() => {
    if (activeTab === "Queue") {
      fetchRequestQueue();
    }
    // console.log("onQueueResponse", onQueueResponse);
  }, [activeTab, onQueueResponse]);

  const getFirst3Lines = (text) => {
    if (!text) return "";
    const lines = text.split("\n");
    let result = [];
    let charCount = 0;
    for (
      let i = 0;
      i < lines.length && result.length < 3 && charCount < 150;
      i++
    ) {
      let line = lines[i];
      // If adding this line exceeds 150 chars, trim it
      if (charCount + line.length > 150) {
        line = line.slice(0, 150 - charCount);
      }
      result.push(line);
      charCount += line.length;
      if (charCount >= 150) break;
    }
    let joined = result.join("\n");
    if (lines.length > 3 || text.length > 150) {
      joined += "...";
    }
    return joined;
  };

  // Add this function above getFilteredQueueRequests
  // const getLatestQueueRequestsBySession = () => {
  //   const latestBySession = {};
  //    (queueRequests || []).forEach((req) => {
  //     if (
  //       !latestBySession[req.session_id] ||
  //       new Date(req.created_at) > new Date(latestBySession[req.session_id].created_at)
  //     ) {
  //       latestBySession[req.session_id] = req;
  //     }
  //   });
  //   return Object.values(latestBySession);
  // };

  // Update getFilteredQueueRequests to use this
  const getFilteredQueueRequests = (paginate = true) => {
    let filtered = queueRequests || [];
    switch (nestedQueueTab) {
      case "This Topic":
        filtered = filtered.filter((req) => req.session_id === currentSession);
        break;
      case "Unread":
        filtered = filtered.filter((req) => !req.is_view);
        break;
      case "Read":
        filtered = filtered.filter((req) => req.is_view);
        break;
      default:
        break;
    }
    if (queueSearch.trim()) {
      const search = queueSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (req) =>
          (req.title && req.title.toLowerCase().includes(search)) ||
          (req.queue_chat?.user_query &&
            req.queue_chat.user_query.toLowerCase().includes(search))
      );
    }
    if (!paginate) return filtered;
    const startIndex = (queuePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getQueueTotalPages = () => Math.ceil((getFilteredQueueRequests(false)?.length || 0) / ITEMS_PER_PAGE);
  const getQueueTotalCount = () => getFilteredQueueRequests(false)?.length || 0;

  const unreadCount = (queueRequests || []).filter(
    (req) => !req.is_view
  ).length;

  const getPopupContent = async (requestId) => {
    const { data: queueRequests, error: requestError } = await supabase
      .from("queue_chat")
      .select("*, path") // Include path column for audio URLs and file URLs
      .eq("queue_request_id", requestId)
      .order("created_at", { ascending: true });

    if (requestError) {
      console.error("Error fetching queue chat:", requestError);
      return;
    }

    // Map the data to handle audio and file messages properly like ReplyModal
    const mappedData =
      queueRequests?.map((item) => {
        const hasAudio =
          item.path &&
          item.path.includes("/audios/") &&
          item.path.trim() !== "";
        const hasFile =
          item.path &&
          !item.path.includes("/audios/") &&
          item.path.trim() !== "";
        const hasText = item.user_query && item.user_query.trim() !== "";

        // Check if it's a file message (with or without text)
        if (hasFile && hasText) {
          // Check if user_query contains file reference
          const isFileReference =
            item.user_query.includes("Shared image:") ||
            item.user_query.includes("Shared file:") ||
            item.user_query.includes(".jpg") ||
            item.user_query.includes(".png") ||
            item.user_query.includes(".pdf") ||
            item.user_query.includes(".doc");

          if (isFileReference) {
            // Pure file message
            return {
              ...item,
              audioUrl: null,
              fileUrl: item.path,
              isFileMessage: true,
              fileName: item.user_query
                .replace("Shared image: ", "")
                .replace("Shared file: ", ""),
              fileType: item.user_query.includes("Shared image:")
                ? "image/"
                : "application/",
            };
          } else {
            // File with text message - show both
            return {
              ...item,
              audioUrl: null,
              fileUrl: item.path,
              isFileMessage: true,
              fileName: item.path.split("/").pop() || "file",
              fileType: item.path.includes("images/")
                ? "image/"
                : "application/",
              hasText: true, // Flag to show text along with file
            };
          }
        }
        // If message has both audio and text, we need to create separate entries
        else if (hasAudio && hasText) {
          return [
            // Audio-only message
            {
              ...item,
              id: `${item.id}_audio`,
              audioUrl: item.path,
              user_query: "", // Empty text for audio-only
              isAudioOnly: true,
            },
            // Text-only message
            {
              ...item,
              id: item.id,
              audioUrl: null,
              path: null,
              isTextOnly: true,
            },
          ];
        } else if (hasAudio && !hasText) {
          // Audio-only message
          return {
            ...item,
            audioUrl: item.path,
            isAudioOnly: true,
          };
        } else {
          // Text-only message
          return {
            ...item,
            audioUrl: item.path, // Keep for backward compatibility
          };
        }
      }) || [];

    // Flatten the array in case we have nested arrays from audio+text messages
    const flattenedData = mappedData.flat();

    setSelectedQueueRequest(flattenedData);
    console.log("Fetched queue data:", flattenedData);
  };

  useEffect(() => {
    if (selectedQueueRequest !== null) {
      setShowQueueModal(true);
    }
  }, [selectedQueueRequest]);

  useEffect(() => {
    if (queueModalData && queueModalData.queueId) {
      handleQueueEyeClick(queueModalData.queueId, queueModalData.queueTitle);
      setShowQueueModal(true);
      setQueueModalData(null); // Reset after opening
    }
  }, [queueModalData]);
  //  console.log("abdul requestId", userId);
  // Eye icon click handler
  const handleQueueEyeClick = async (requestId, requestTitle) => {
    // console.log(request);
    //  console.log("abdul requestId", requestId);
    setQueueViewTitle(requestTitle);
    setQueueView(requestId);
    getPopupContent(requestId);
    // Update response_time to current time when viewed
    await supabase
      .from("queue_request")
      .update({ response_time: new Date().toISOString() })
      .eq("id", requestId);

    isViewQueue(requestId);
    // Refresh queue list to update sorting
    fetchRequestQueue();
  };

  const handleMeetingEyeClick = async (requestId, requestTitle) => {
    // console.log(request);
    // is_view
    // setSelectedMeeting(requestId);
    await supabase
      .from("meeting_summaries")
      .update({ is_view: true, updated: "now()" })
      .eq("id", requestId)
      .eq("user_id", userId); // ✅ Add user filter here
    setShowMeetingModal(true);
  };

  useEffect(() => {
    if (queueView !== null && showQueueModal) {
      getPopupContent(queueView);
      // isViewQueue(queueView);
    }
  }, [onQueueResponse, onNotificationResponse]);

  useEffect(() => {
    if (activeTab === "Queue") {
      fetchRequestQueue();
    }
  }, [activeTab, onQueueResponse, queueRefreshKey, onNotificationResponse]); // <-- Add queueRefreshKey here
  // console.log("Selected Queue Request:", selectedQueueRequest);
  const playAudio = async (index, TextToPlay, action) => {
    const url = "https://api.deepgram.com/v1/speak?model=aura-luna-en";
    const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY; // Replace with your actual API key

    if (action === "stop") {
      if (window.currentAudio) {
        window.currentAudio.pause();
        window.currentAudio.currentTime = 0;
        setPlayingId(null);
        setPlaying(false);
      }
      return;
    } else {
      if (isPlaying) {
        return;
      } else {
        setPlayingId(index);
        setPlaying(true);

        try {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Token ${apiKey}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ text: TextToPlay }),
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.statusText}`);
          }

          const blob = await response.blob();
          const audioUrl = URL.createObjectURL(blob);
          const audio = new Audio(audioUrl);

          window.currentAudio = audio; // Store the audio object globally

          audio.play();
          audio.onended = () => {
            setPlaying(false);
            setPlayingId(null); // Reset state when audio ends
          };
        } catch (error) {
          console.error("Error:", error);
          setPlayingId(null);
          setPlaying(false);
        }
      }
    }
  };

  // Add audio processing functions for Queue Modal
  const audioToText = async (audioInput) => {
    try {
      // Get fresh session token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No valid session found. Please log in again.");
      }

      const formData = new FormData();
      formData.append("file", audioInput, "recording.mp3");

      const response = await fetch(zoomey_api_url + "/speech-to-text", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          // Don't include Content-Type - browser sets it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Upload failed: ${response.statusText} - ${
            errorData.error?.message || ""
          }`
        );
      }

      const data = await response.json();
      return data.text;
    } catch (error) {
      console.error("Error transcribing audio:", error);
      return null;
    }
  };

  // Handle audio input for Queue Modal
  // useEffect(() => {
  //   if (audioInput) {
  //     handleQueueAudioMessage();
  //   }
  // }, [audioInput]);

  // useEffect(() => {
  //   if (audioInput) {
  //     (async () => {
  //       setIsAudioProcessing(true);
  //       const transcript = await audioToText(audioInput);
  //       if (transcript) {
  //         setReplyMessage((prev) =>
  //           prev ? prev + " " + transcript : transcript
  //         );
  //       }
  //       setIsAudioProcessing(false);
  //       setAudioInput(null);
  //     })();
  //   }
  // }, [audioInput]);

  // Single handler for audioInput: either copy-to-text or full queue processing
  useEffect(() => {
    if (!audioInput) return;
    (async () => {
      try {
        if (copyToTextArea) {
          setIsAudioProcessing(true);
          const transcript = await audioToText(audioInput);
          if (transcript) {
            setReplyMessage((prev) =>
              prev ? prev + " " + transcript : transcript
            );
            // focus textarea so user can edit immediately
            if (textareaRef.current) textareaRef.current.focus();
          }
          setIsAudioProcessing(false);
          setAudioInput(null);
          setCopyToTextArea(false);
          return;
        }
        // Normal flow: process and send (handleQueueAudioMessage reads audioInput)
        await handleQueueAudioMessage();
      } catch (err) {
        console.error("Audio processing error:", err);
        setIsAudioProcessing(false);
        setAudioInput(null);
        setCopyToTextArea(false);
      }
    })();
  }, [audioInput, copyToTextArea]);

  // If Record requests cancel, clear pending audio and flags
  useEffect(() => {
    if (!cancelAudio) return;
    setAudioInput(null);
    setIsAudioProcessing(false);
    setCopyToTextArea(false);
    setCancelAudio(false);
  }, [cancelAudio]);

  const handleQueueAudioMessage = async () => {
    try {
      if (audioInput != null) {
        setIsLoading(true);
        setIsAudioProcessing(true);
        console.log("Processing queue audio input...");

        // Show temporary audio processing message in chat
        const tempProcessingId = `temp-${Date.now()}`;
        setSelectedQueueRequest((prev) => {
          const currentList = prev || [];
          return [
            ...currentList,
            {
              id: tempProcessingId,
              user_query: "", // Empty query for now
              audioUrl: null,
              isProcessing: true, // Flag to show processing state
              createdAt: new Date().toISOString(),
              response: null,
            },
          ];
        });

        // Convert audio to text
        const audioText = await audioToText(audioInput);
        console.log("Queue audio transcribed:", audioText);

        // Combine input area text + transcript
        let finalText = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
        mentionsInputRef.current?.clearMention();
        if (audioText) {
          finalText = finalText
            ? `${finalText} ${audioText}` // Combine both
            : audioText; // Only transcript if input empty
        }

        if (audioText) {
          // Upload audio to Supabase storage
          let supabaseAudioUrl = null;
          try {
            supabaseAudioUrl = await uploadAudioAndGetUrl(
              queueView,
              userId,
              audioInput
            );
            console.log("Queue audio uploaded to:", supabaseAudioUrl);
          } catch (uploadError) {
            console.error("Queue audio upload failed:", uploadError);
            // Continue without audio URL if upload fails
          }

          // Store the queue chat with audio URL
          const dataGet = await storeQueueChat(
            queueView,
            finalText, // <-- use combined text here
            supabaseAudioUrl
          );
          console.log("Queue chat stored:", dataGet);

          // Remove temporary processing message and add real messages
          setSelectedQueueRequest((prev) => {
            const currentList = prev || [];
            // Remove temp processing message
            const withoutTemp = currentList.filter(
              (item) => item.id !== tempProcessingId
            );
            // Add BOTH audio message AND text message (separate bubbles like ReplyModal)
            return [
              ...withoutTemp,
              // Audio message bubble (separate)
              {
                id: `${dataGet[0].id}_audio`,
                user_query: "", // Empty text for audio-only message
                audioUrl: supabaseAudioUrl,
                path: supabaseAudioUrl,
                createdAt: new Date().toISOString(),
                response: null,
                isAudioOnly: true, // Flag to identify audio-only message
              },
              // Text message bubble (separate)
              {
                id: dataGet[0].id,
                user_query: audioText,
                audioUrl: null, // No audio for text message
                path: null,
                createdAt: new Date().toISOString(),
                response: null,
                isTextOnly: true, // Flag to identify text-only message
              },
            ];
          });

          setIsAudioProcessing(false);
          setReplyMessage("");

          const payload = {
            user_id: userId,
            session_id: queueView.toString(),
            store_id: dataGet[0].id.toString(),
            user_query: finalText, // <-- use combined text here
            mode: selectedMode,
            customContextLimits: getContextLimitsForPayload(),
          };

          // Send to API
          const response = await fetch(zoomey_api_url + "/single_query", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiToken}`,
            },
            body: JSON.stringify(payload),
          });

          if (response.status === 200) {
            console.log("Queue audio API call successful");
            setReplyMessage(""); // clear input area
            if (queueView && userId) {
              const key = `queue_${queueView}_${userId}`;
              await deleteDraft(key); // <-- Always delete draft after send
            }
            // Refresh the full conversation after response
            setTimeout(() => {
              getPopupContent(queueView); // Use existing getPopupContent function
            }, 1000);
          } else {
            console.error(
              "Queue audio API call failed with status:",
              response.status
            );
            // alert("Something went wrong with your server.");
          }
        }
      }
    } catch (error) {
      console.error("Queue audio processing error:", error);
      // alert("Failed to process audio. Please try again.");
      // Remove temp processing message on error
      setSelectedQueueRequest((prev) => {
        const currentList = prev || [];
        return currentList.filter((item) => !item.isProcessing);
      });
    } finally {
      setIsLoading(false);
      setIsAudioProcessing(false);
      setAudioInput(null);
      setReplyMessage(""); // clear after processing
      if (queueView && userId) {
        const key = `queue_${queueView}_${userId}`;
        await deleteDraft(key); // <-- Always delete draft after send
      }
    }
  };

  // Add this function to handle audio recording stop
  const handleQueueAudioRecorded = async (audioUrl) => {
    try {
      setIsLoading(true);
      console.log("Queue audio recorded:", audioUrl);
    } catch (error) {
      console.error("Error handling queue recorded audio:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplyClick = async (notification) => {
    setSelectedNotification(notification);
    setReplyMessage(notification.user_query || ""); // Set reply message to previous user_query if it exists, otherwise reset
    setShowModal(true);
    if (!notification.is_read) {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notification.id)
        .eq("user_id", userId);

      if (error) {
        console.error("Error updating notification:", error);
      }
      // Update local state and trigger parent re-render
      const updatedNotification = { ...notification, is_read: true };
      const updatedNotifications = notificationsData.map((notif) =>
        notif.id === notification.id ? updatedNotification : notif
      );
      setNotificationsData(updatedNotifications);
      onNotificationUpdate(updatedNotification);
    }
  };

  const handleSendReply = (notificationId) => {
    if (replyMessage.trim() === "") return; // Prevent sending if reply message is blank
    setShowModal(false);
    setReplyMessage("");
    // Add logic to remove the notification
  };
  const createNotificationSession = async (newName, setError) => {
    if (!newName.trim()) {
      setError("Topic name cannot be blank.");
      return;
    }
    if (selectedNotification) {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (session) {
        const payload = {
          session_name: newName,
          user_id: session.user.id,
          notification_id: selectedNotification?.id,
          message: selectedNotification?.message,
        };
        fetch(`${zoomey_api_url}/notification_new_session_create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((data) => {
            console.log("Response from server:", data);
            if (data.status == "success") {
              setShowFormPopup(false);
              resetSession(data.session_id);
            } else {
              setError(data.message || "An error occurred");
            }
          })
          .catch((error) => {
            console.error("Error creating notification session:", error);
            setError("Failed to create topic. Please try again.");
          });
      }

      // const {
      //   data: { session },
      //   error,
      // } = await supabase.auth.getSession();
      // if (session) {
      //   let createdSession;
      //   try {
      //     createdSession = await createChatSession(
      //       "notification",
      //       newName,
      //       session.user.id,
      //       null
      //     ); // Pass parentId if available
      //     firstMessage(
      //       selectedNotification.message,
      //       workflowUrl,
      //       createdSession[0].id,
      //       createdSession[0].name
      //     ); // Call firstMessage with the created session ID

      //     // Update the notifications table with the new session ID
      //     const { error: updateError } = await supabase
      //       .from("notifications")
      //       .update({ session_id: createdSession[0].id, status: true })
      //       .eq("id", selectedNotification.id);

      //     if (updateError) {
      //       console.error("Error updating notification:", updateError);
      //       setError("An error occurred while updating the notification.");
      //     }
      //     await supabase
      //       .from("chat")
      //       .insert([
      //         {
      //           chat_session_id: createdSession[0].id,
      //           is_query: false,
      //           content: selectedNotification.message,
      //         },
      //         {
      //           chat_session_id: createdSession[0].id,
      //           is_query: false,
      //           content: "Hi! 👋 How can I help you today?",
      //         },
      //       ])
      //       .then(() => {
      //         resetSession(createdSession[0].id); // Call resetSession with the created session ID
      //       });
      //   } catch (err) {
      //     console.error("Error creating or renaming session:", err);
      //     setError("An error occurred while creating or renaming the session.");
      //   }
      // }
    }
  };

  const handleDeleteQueue = async (queueId) => {
    setDeletingQueueId(queueId); // Start animation
    try {
      // Delete associated queue_chat records first
      await supabase.from("queue_chat").delete().eq("queue_request_id", queueId);
      // Delete the queue_request
      await supabase.from("queue_request").delete().eq("id", queueId);
      // Mark any associated chat records as deleted
      await supabase
        .from("chat")
        .update({ is_delete: true })
        .eq("queue_id", queueId);
      setDeletingQueueId(null); // Reset after delete
      fetchRequestQueue(); // Refresh list
    } catch (err) {
      console.error("Error deleting queue:", err);
      setDeletingQueueId(null);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId)
        .eq("user_id", userId); // ✅ Add user filter here

      if (error) {
        console.error("Error deleting notification:", error);
        return;
      }

      // Update the notifications state to remove the deleted notification
      const updatedNotifications = notifications.filter(
        (notification) => notification.id !== notificationId
      );
      setNotificationsData(updatedNotifications);
      clearNotifications(updatedNotifications); // Update the notifications count in the parent component
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const clearAllQueueRequest = async () => {
    try {
      // Delete queue_request for current user only
      await supabase
        .from("queue_request")
        .delete()
        .eq("user_id", userId);

      // Delete queue_chat for current user only
      await supabase
        .from("queue_chat")
        .delete()
        .eq("user_id", userId);

      // Mark chat records with queue_id as deleted for current user only
      await supabase
        .from("chat")
        .update({ is_delete: true })
        .eq("user_id", userId)
        .not("queue_id", "is", null);

      // Clear local state
      setQueueRequests([]);
    } catch (err) {
      console.error("Error clearing queue requests:", err);
    }
  };
  const clearAllNotifications = async () => {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error getting session:", sessionError);
        return;
      }

      if (session) {
        const { error } = await supabase
          .from("notifications")
          .delete()
          // .eq("user_id", session.user.id);
          .eq("user_id", userId); // ✅ Use passed userId instead of session.user.id

        if (error) {
          console.error("Error deleting notifications:", error);
          return;
        }

        // Clear notifications state
        setNotificationsData([]);
      }
    } catch (err) {
      console.error("Error clearing notifications:", err);
    }
  };

  // Meeting tab states and helpers
  const fetchMeetings = async () => {
    setIsLoadingMeetings(true);
    try {
      const { data, error } = await supabase
        .from("meeting_summaries")
        .select(
          "id, meeting_title, short_summary, meeting_date, is_view, updated, is_delete"
        )
        .eq("user_id", userId) // ✅ Add user filter here
        .order("meeting_date", { ascending: false });
      if (error) throw error;
      // Only show meetings where is_delete is false
      setMeetings((data || []).filter((m) => !m.is_delete));
    } catch (err) {
      setMeetings([]);
    } finally {
      setIsLoadingMeetings(false);
    }
  };

  useEffect(() => {
    if (activeTab === "Meeting") {
      fetchMeetings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (onMeetingResponse) {
      fetchMeetings();
    }
  }, [onMeetingResponse]);

  const getFilteredMeetings = (paginate = true) => {
    let filtered = meetings || [];
    if (meetingNestedTab === "Read")
      filtered = filtered.filter((m) => m.is_view);
    if (meetingNestedTab === "Unread")
      filtered = filtered.filter((m) => !m.is_view);
    if (meetingSearch.trim()) {
      const s = meetingSearch.trim().toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.meeting_title?.toLowerCase().includes(s) ||
          m.short_summary?.toLowerCase().includes(s)
      );
    }
    if (!paginate) return filtered;
    const startIndex = (meetingPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getMeetingTotalPages = () => Math.ceil((getFilteredMeetings(false)?.length || 0) / ITEMS_PER_PAGE);
  const getMeetingTotalCount = () => getFilteredMeetings(false)?.length || 0;

  const handleDeleteMeeting = async (meeting) => {
    setIsLoadingMeetings(true);
    const { error } = await supabase
      .from("meeting_summaries")
      .update({ is_delete: true })
      .eq("id", meeting.id)
      .eq("user_id", userId);
    if (error) {
      console.error("Error deleting meeting:", error);
    } else {
      setMeetings((prev) =>
        Array.isArray(prev) ? prev.filter((m) => m.id !== meeting.id) : []
      );
    }
    setIsLoadingMeetings(false);
  };

  const handleClearAllMeetings = async () => {
    setIsLoadingMeetings(true);
    await supabase
      .from("meeting_summaries")
      .update({ is_delete: true })
      .eq("is_delete", false)
      .eq("user_id", userId); // ✅ Add user filter here
    fetchMeetings();
  };

  return (
    <>
      {/* <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .audio-processing-spinner {
            animation: spin 1s linear infinite;
          }
        `}
      </style> */}
      <div className="nav-pills nav-fill">
        {/* Tabs Nav */}
        <ul
          className="nav nav-tabs"
          style={{
            borderColor: "var(--border-color)",
            height: "81px",
            boxSizing: "border-box",
            padding: "20px 16px",
            marginBottom: 0,
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}
        >
          <li className="nav-item nav-item-tab">
            <button
              className={`nav-link ${
                activeTab === "notifications" ? "active" : ""
              }`}
              onClick={() => setActiveTab("notifications")}
            >
              {/* <i className="bi bi-bell"></i> */}
              Notifications
            </button>
          </li>
          <li className="nav-item nav-item-tab">
            <button
              className={`nav-link ${activeTab === "Queue" ? "active" : ""}`}
              onClick={() => setActiveTab("Queue")}
            >
              Queue
            </button>
          </li>
          <li className="nav-item nav-item-tab">
            <button
              className={`nav-link ${activeTab === "Meeting" ? "active" : ""}`}
              onClick={() => setActiveTab("Meeting")}
            >
              {/* <i className="bi bi-calendar-event"></i> */}
              Meeting
            </button>
          </li>
          <li className="nav-item nav-item-tab">
            <button
              className={`nav-link ${activeTab === "Voice" ? "active" : ""}`}
              onClick={() => setActiveTab("Voice")}
            >
              {isVoiceActive && <span className="voice-tab-indicator"></span>}
              Voice
            </button>
          </li>
        </ul>

        {/* Tabs Content */}
        <div className="sidebar-tab-content">
          {/* Notifications Tab Content */}
          {activeTab === "notifications" && (
            <div className="sidebar-tab-panel">
              <div className="px-3 py-2 border-bottom">
                {/* Nested tabs for notifications */}
                <ul className="nav nav-item-tab-child nav-pills mb-2">
                  {["All", "Unread", "Read"].map((tab) => (
                    <li className="nav-item" key={tab}>
                      <button
                        className={`nav-link ${
                          nestedNotifTab === tab ? "active" : ""
                        }`}
                        onClick={() => setNestedNotifTab(tab)}
                      >
                        {tab}
                        {tab === "Unread" &&
                          baseNotifications.filter((n) => !n.is_read).length >
                            0 && (
                            <span className="badge bg-danger ms-1 card-breadcrumb-custom">
                              {
                                baseNotifications.filter((n) => !n.is_read)
                                  .length
                              }
                            </span>
                          )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-3 py-1 border-bottom">
                {/* Search input for notifications */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    className="form-control mb-2 mt-2 search-input-box"
                    placeholder="Search notifications..."
                    value={notifSearch}
                    onChange={(e) => setNotifSearch(e.target.value)}
                    style={{ width: "100%", marginRight: "8px" }}
                  />

                  <button
                    className="btn btn-link mb-2 mt-2 text-end card-clear-all"
                     style={{
                      //  width: "30%" ,
                       paddingRight: "10px 14px 10px 14px"}}
                    onClick={() => setShowConfirmationPopup(true)}
                  >
                    Clear all
                  </button>
                </div>
              </div>

              {/* Notification list with color coding */}
              <div
                ref={notificationListRef}
                className="list-group notification-list p-3 custom-scrollbar sidebar-list-container"
              >
                {isLoadingNotifications ? (
                  <div className="wave-loader-container">
                    <div className="wave-loader">
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                    </div>
                    <span className="wave-loader-text">Loading notifications...</span>
                  </div>
                ) : getFilteredNotifications().length > 0 ? (
                  getFilteredNotifications().map((notification, index) => (
                    <div
                      key={notification.id ?? index}
                      className={`list-group-item list-group-item-action right-sidebar-card mb-3 rounded border-1 p-3`}
                      style={{
                        backgroundColor: "var(--input-box-bg) !important",
                        borderColor: "var(--border-color) !important",
                      }}
                      onClick={() => handleReplyClick(notification)}
                    >
                      <div className="d-flex w-100 justify-content-between mb-1">
                        <label
                          className="mb-1"
                          style={{
                            paddingTop: "10px",
                            fontSize: "var(--font-size)",
                            fontWeight: "var(--font-weight)",
                          }}
                        >
                          {notification.title}
                        </label>
                        <div
                          className="d-flex align-items-center"
                          style={{ fontSize: "12px !important" }}
                        >
                          {/* Play/Stop Button */}
                          {isPlayingId === index && isPlaying ? (
                            <i
                              title="Stop Audio"
                              className="ms-3 bi bi-stop"
                              onClick={async () =>
                                playAudio(index, notification.message, "stop")
                              }
                            ></i>
                          ) : (
                            <i
                              title="Play Audio"
                              className="ms-3 bi bi-play"
                              onClick={async (e) => {
                                e.stopPropagation();
                                playAudio(index, notification.message, "play");
                              }}
                              disabled={
                                isPlayingId !== null && isPlayingId !== index
                              }
                            ></i>
                          )}
                          {/* Eye Button */}
                          <button
                            className="btn btn-link"
                            onClick={() => handleReplyClick(notification)}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          {/* Delete Button */}
                          <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                          >
                            <i
                              title="Delete Notification"
                              className="bi bi-x"
                              style={{ fontSize: "1.5em" }}
                            ></i>
                          </button>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div
                          style={{ fontSize: "1em" }}
                          dangerouslySetInnerHTML={{
                            __html: formatResponseMessage(
                              truncateMessage(notification.message, 15)
                            ),
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        {notification.is_read ? (
                          <span className="message-success-label">Read</span>
                        ) : (
                          <span className="message-error-label">Unread</span>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="mb-1 me-1">
                            {timeAgo(notification.created_at)}
                          </small>
                          {/* Session buttons */}
                          {notification.session_id == null ? (
                            <button
                              className="btn btn-link p-0 m-1"
                              style={{
                                // backgroundColor: "#F8F9FA",
                                color: "#18181B",
                                padding: "5px",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="create chat topic"
                              onClick={(e) => {
                                setSelectedNotification(notification);
                                setShowFormPopup(true);
                                e.stopPropagation();
                              }}
                            >
                              <i className="bi bi-chat-left"></i>
                            </button>
                          ) : (
                            <button
                              className="btn btn-link p-0 m-1"
                              style={{
                                // backgroundColor: "#F8F9FA",
                                color: "#18181B",
                                padding: "5px",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={(e) => {
                                resetSession(notification.session_id);
                                e.stopPropagation();
                              }}
                            >
                              <i className="bi bi-chat-left-text"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="voice-empty-state">
                    <i className="bi bi-bell"></i>
                    <p>No notifications yet</p>
                    <small>New notifications will appear here</small>
                  </div>
                )}
              </div>

              {/* Notifications Pagination */}
              {!isLoadingNotifications && getNotifTotalCount() > 0 && (
                <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center sidebar-pagination">
                  <small className="text-muted">
                    {getNotifTotalCount() > ITEMS_PER_PAGE ? (
                      <>Showing {((notifPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(notifPage * ITEMS_PER_PAGE, getNotifTotalCount())} of {getNotifTotalCount()}</>
                    ) : (
                      <>{getNotifTotalCount()} notification{getNotifTotalCount() !== 1 ? 's' : ''}</>
                    )}
                  </small>
                  {getNotifTotalPages() > 1 && (
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${notifPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setNotifPage(p => Math.max(1, p - 1))} disabled={notifPage === 1}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: Math.min(getNotifTotalPages(), 5) }, (_, i) => {
                          let pageNum;
                          const totalPages = getNotifTotalPages();
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (notifPage <= 3) pageNum = i + 1;
                          else if (notifPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = notifPage - 2 + i;
                          return (
                            <li key={pageNum} className={`page-item ${notifPage === pageNum ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setNotifPage(pageNum)}>{pageNum}</button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${notifPage === getNotifTotalPages() ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setNotifPage(p => Math.min(getNotifTotalPages(), p + 1))} disabled={notifPage === getNotifTotalPages()}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Queue Tab Content */}
          {activeTab === "Queue" && ( 
            <div className="sidebar-tab-panel">
              <div className="px-3 py-2 border-bottom">
                {/* Nested Tabs */}
                <ul className="nav nav-item-tab-child nav-pills mb-2">
                  {["All", "This Topic", "Unread", "Read"].map((tab) => (
                    <li className="nav-item" key={tab}>
                      <button
                        className={`nav-link ${
                          nestedQueueTab === tab ? "active" : ""
                        }`}
                        onClick={() => {
                          // Refresh currentSession from cookie when switching to "This Topic" tab
                          if (tab === "This Topic") {
                            setCurrentSession(Cookies.get("chat_session_id"));
                          }
                          setNestedQueueTab(tab);
                        }}
                      >
                        {tab}
                        {tab === "Unread" && unreadCount > 0 && (
                          <span className="badge bg-danger ms-1 card-breadcrumb-custom">
                            {unreadCount}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-3 py-1 border-bottom">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    className="form-control mb-2 mt-2 search-input-box"
                    placeholder="search..."
                    value={queueSearch}
                    onChange={(e) => setQueueSearch(e.target.value)}
                    style={{ width: "100%", marginRight: "8px" }}
                  />

                  <button
                    className="btn btn-link mb-2 mt-2 text-end card-clear-all"
                     style={{
                      //  width: "30%" ,
                       paddingRight: "10px 14px 10px 14px"}}
                    onClick={() => setShowQueueConfirmationPopup(true)}
                  >
                    Clear all
                  </button>
                </div>
              </div>

              {/* Dynamic Queue Requests */}
              <div
                ref={queueListRef}
                className="list-group notification-list p-3 custom-scrollbar sidebar-list-container"
              >
                {isLoadingQueue ? (
                  <div className="wave-loader-container">
                    <div className="wave-loader">
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                      <div className="wave-bar"></div>
                    </div>
                    <span className="wave-loader-text">Loading queue...</span>
                  </div>
                ) : (
                  <div className="list-group">
                    {getFilteredQueueRequests().length > 0 ? (
                      getFilteredQueueRequests().map((request) => (
                        <div
                          key={request.id}
                          className={`list-group-item list-group-item-action right-sidebar-card mb-3 rounded border-1 p-3 ${
                            request.is_view ? "bg-read" : "bg-unread"
                          } ${
                            deletingQueueId === request.id
                              ? "queue-crack-animate"
                              : ""
                          } `}
                          style={{
                            cursor: "pointer",
                            backgroundColor: "var(--input-box-bg) !important",
                            borderColor: "var(--border-color) !important",
                          }}
                          onClick={() =>
                            handleQueueEyeClick(request.id, request.title)
                          }
                        >
                          <div className="d-flex justify-content-between mb-1">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                maxWidth: "180px",
                              }}
                            >
                              <label
                                className="mb-1"
                                style={{
                                  paddingTop: "10px",
                                  fontSize: "var(--font-size)",
                                  fontWeight: "var(--font-weight)",
                                }}
                                title={
                                  request.title || `Request #${request.id}`
                                }
                              >
                                {request.title || `Request #${request.id}`}
                              </label>
                            </div>
                            <small>
                              <button
                                className="btn pt-0 pb-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQueueEyeClick(
                                    request.id,
                                    request.title
                                  );
                                }}
                                title="View details"
                              >
                                <i
                                  className="bi bi-eye"
                                  style={{ color: "var(--text) !important" }}
                                ></i>
                              </button>
                              {/* { getTimeZone(request?.response_time)} */}

                              <button
                                className="btn btn-link p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteQueue(request.id);
                                }}
                              >
                                <i
                                  className="bi bi-x"
                                  style={{ fontSize: "1.5em" }}
                                ></i>
                              </button>
                            </small>
                          </div>
                          <div className="mb-3">
                            <p className="fontsizeclass">
                              {getFirst3Lines(request.queue_chat?.user_query) ||
                                "No query available"}
                            </p>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            {request.is_view ? (
                              <span className="message-success-label">
                                Read
                              </span>
                            ) : request.completed ? (
                              <span className="message-error-label">
                                Unread
                              </span>
                            ) : (
                              <span className="message-warning-label">
                                Thinking
                              </span>
                            )}
                            {/* <span
                              className={`badge fontsizeclass ${
                                request.is_view ? "bg-dark" : "bg-danger"
                              }`}
                            >
                              {request.is_view
                                ? "Read"
                                : request.completed
                                ? "Unread"
                                : "Thinking"}
                            </span> */}
                            <div className="d-flex justify-content-between align-items-center">
                              <small
                                className={`me-2 ${
                                  request.session_id == null ? "mb-1" : "mt-1"
                                }`}
                              >
                                {timeAgo(request?.response_time)}
                              </small>
                              {request.session_id == null ? (
                                <button
                                  className="btn btn-link p-0"
                                  style={{
                                    // backgroundColor: "#F8F9FA",
                                    color: "#18181B",
                                    padding: "5px",
                                    width: "24px",
                                    height: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsQueueStandalone(true);
                                    setShowQueueFormPopup(true);
                                    setQueueSelectId(request.id);
                                  }}
                                >
                                  <i className="bi bi-chat-left"></i>
                                </button>
                              ) : (
                                <>
                                  <span className="mt-2 d-flex justify-content-between align-items-center">
                                    <button
                                      className="btn btn-link p-0"
                                      style={{
                                        // backgroundColor: "#F8F9FA",
                                        color: "#18181B",
                                        padding: "5px",
                                        width: "24px",
                                        height: "24px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                      title="create chat topic"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIsQueueStandalone(false);
                                        setShowQueueFormPopup(true);
                                        setQueueSelectId(request.id);
                                        console.log(
                                          request.session_id,
                                          "abdilsssssssssss"
                                        );
                                      }}
                                    >
                                      <i className="bi bi-chat-left"></i>
                                    </button>
                                    <button
                                      className="btn btn-link p-0 mt-"
                                      style={{
                                        // backgroundColor: "#F8F9FA",
                                        color: "#18181B",
                                        padding: "5px",
                                        width: "24px",
                                        height: "24px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        resetSession(request.session_id);
                                      }}
                                    >
                                      <i className="bi bi-chat-left-text"></i>
                                    </button>
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="voice-empty-state">
                        <i className="bi bi-inbox"></i>
                        <p>No queue requests yet</p>
                        <small>Queue requests from your sessions will appear here</small>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Queue Pagination */}
              {!isLoadingQueue && getQueueTotalCount() > 0 && (
                <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center sidebar-pagination">
                  <small className="text-muted">
                    {getQueueTotalCount() > ITEMS_PER_PAGE ? (
                      <>Showing {((queuePage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(queuePage * ITEMS_PER_PAGE, getQueueTotalCount())} of {getQueueTotalCount()}</>
                    ) : (
                      <>{getQueueTotalCount()} request{getQueueTotalCount() !== 1 ? 's' : ''}</>
                    )}
                  </small>
                  {getQueueTotalPages() > 1 && (
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${queuePage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setQueuePage(p => Math.max(1, p - 1))} disabled={queuePage === 1}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: Math.min(getQueueTotalPages(), 5) }, (_, i) => {
                          let pageNum;
                          const totalPages = getQueueTotalPages();
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (queuePage <= 3) pageNum = i + 1;
                          else if (queuePage >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = queuePage - 2 + i;
                          return (
                            <li key={pageNum} className={`page-item ${queuePage === pageNum ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setQueuePage(pageNum)}>{pageNum}</button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${queuePage === getQueueTotalPages() ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setQueuePage(p => Math.min(getQueueTotalPages(), p + 1))} disabled={queuePage === getQueueTotalPages()}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Meeting Tab Content */}
          {activeTab === "Meeting" && (
            <div className="sidebar-tab-panel">
              <div className="px-3 py-2 border-bottom">
                {/* Nested tabs for meetings */}
                <ul className="nav nav-item-tab-child nav-pills mb-2">
                  {["All", "Unread", "Read"].map((tab) => (
                    <li className="nav-item" key={tab}>
                      <button
                        className={`nav-link ${
                          meetingNestedTab === tab ? "active" : ""
                        }`}
                        onClick={() => setMeetingNestedTab(tab)}
                      >
                        {tab}
                        {tab === "Unread" &&
                          (meetings || []).filter((m) => !m.is_view)
                            .length > 0 && (
                            <span className="badge bg-danger ms-1 card-breadcrumb-custom">
                              {
                                (meetings || []).filter((m) => !m.is_view)
                                  .length
                              }
                            </span>
                          )}
                      </button>
                    </li>
                  ))}
                </ul>
                {/* Search input for meetings */}
              </div>

              <div className="px-3 py-1 border-bottom">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    className="form-control mb-2 mt-2 search-input-box"
                    placeholder="Search meetings..."
                    value={meetingSearch}
                    onChange={(e) => setMeetingSearch(e.target.value)}
                    style={{ width: "100%", marginRight: "8px" }}
                  />
                  <button
                    className="btn btn-link mb-2 mt-2 text-end card-clear-all"
                    style={{
                      //  width: "30%" ,
                       paddingRight: "10px 14px 10px 14px"}}
                    onClick={() => setShowMeetingConfirmationPopup(true)}
                    disabled={isLoadingMeetings}
                  >
                    Clear all
                  </button>
                </div>
              </div>
              {/* Loader spinner */}
              {isLoadingMeetings ? (
                <div className="wave-loader-container">
                  <div className="wave-loader">
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                  </div>
                  <span className="wave-loader-text">Loading meetings...</span>
                </div>
              ) : (
                <div
                  ref={meetingListRef}
                  className="list-group notification-list p-3 custom-scrollbar sidebar-list-container"
                >
                  {Array.isArray(getFilteredMeetings()) &&
                  getFilteredMeetings().length > 0 ? (
                    getFilteredMeetings().map((meeting, index) => (
                      <div
                        key={
                          meeting.id ??
                          meeting.meeting_title + meeting.meeting_date
                        }
                        className={`list-group-item list-group-item-action right-sidebar-card mb-3 rounded border-1 p-3`}
                        style={{
                          backgroundColor: "var(--input-box-bg) !important",
                          borderColor: "var(--border-color) !important",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          handleMeetingEyeClick(
                            meeting.id,
                            meeting.meeting_title
                          );
                          setSelectedMeeting(meeting);
                        }}
                      >
                        <div className="d-flex w-100 justify-content-between mb-1">
                          <label
                            className="mb-1"
                            style={{
                              paddingTop: "10px",
                              fontSize: "var(--font-size)",
                              fontWeight: "var(--font-weight)",
                            }}
                          >
                            {meeting.meeting_title &&
                            meeting.meeting_title.length > 40
                              ? meeting.meeting_title.slice(0, 40) + "..."
                              : meeting.meeting_title}
                          </label>
                          <div
                            className="d-flex align-items-center"
                            style={{ fontSize: "12px !important" }}
                          >
                            {/* Remove button */}
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMeeting(meeting);
                              }}
                              disabled={isLoadingMeetings}
                            >
                              <i
                                title="Remove"
                                className="bi bi-x"
                                style={{
                                  fontSize: "1.5em",
                                  color: "var(--text)",
                                }}
                              ></i>
                            </button>
                          </div>
                        </div>
                        {/* Date bottom left, chat icon absolute bottom right */}
                        {/* <div className="d-flex align-items-center mt-1" style={{ minHeight: "24px" }}>
                          <small>{formatMeetingDate(meeting.meeting_date)}</small>
                        </div> */}

                        <div
                          className="mb-3"
                          style={{
                            whiteSpace: "pre-line",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {meeting.short_summary &&
                          meeting.short_summary.length > 80
                            ? meeting.short_summary.slice(0, 80) + "..."
                            : meeting.short_summary}
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          {meeting.is_thinking ? (
                            <span className="message-warning-label" style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                              Thinking
                              <span style={{ display: "flex", gap: "2px", marginLeft: "2px" }}>
                                <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "currentColor", animation: "bounce 1.4s infinite ease-in-out" }}></span>
                                <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "currentColor", animation: "bounce 1.4s infinite ease-in-out", animationDelay: "-0.32s" }}></span>
                                <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "currentColor", animation: "bounce 1.4s infinite ease-in-out", animationDelay: "-0.16s" }}></span>
                              </span>
                            </span>
                          ) : meeting.is_view ? (
                            <span className="message-success-label">Read</span>
                          ) : (
                            <span className="message-error-label">Unread</span>
                          )}

                          <div className="d-flex justify-content-between align-items-center">
                            <small className="mb-1 me-2">
                              {formatMeetingDate(meeting.meeting_date)}
                            </small>
                            <button
                              className="btn btn-link p-0"
                              style={{
                                color: "#18181B",
                                right: "12px",
                                bottom: "8px",
                                padding: "2px 4px",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "none",
                                border: "none",
                              }}
                              title="Create chat topic"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMeetingSessionMeetingId(meeting.id);
                                setSelectedMeeting(meeting);
                                setShowMeetingSessionModal(true);
                              }}
                            >
                              <i className="bi bi-chat-left"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="voice-empty-state">
                      <i className="bi bi-calendar-event"></i>
                      <p>No meetings yet</p>
                      <small>Connect Fireflies to see your meeting summaries</small>
                    </div>
                  )}
                </div>
              )}

              {/* Meetings Pagination */}
              {!isLoadingMeetings && getMeetingTotalCount() > 0 && (
                <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center sidebar-pagination">
                  <small className="text-muted">
                    {getMeetingTotalCount() > ITEMS_PER_PAGE ? (
                      <>Showing {((meetingPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(meetingPage * ITEMS_PER_PAGE, getMeetingTotalCount())} of {getMeetingTotalCount()}</>
                    ) : (
                      <>{getMeetingTotalCount()} meeting{getMeetingTotalCount() !== 1 ? 's' : ''}</>
                    )}
                  </small>
                  {getMeetingTotalPages() > 1 && (
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${meetingPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setMeetingPage(p => Math.max(1, p - 1))} disabled={meetingPage === 1}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: Math.min(getMeetingTotalPages(), 5) }, (_, i) => {
                          let pageNum;
                          const totalPages = getMeetingTotalPages();
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (meetingPage <= 3) pageNum = i + 1;
                          else if (meetingPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = meetingPage - 2 + i;
                          return (
                            <li key={pageNum} className={`page-item ${meetingPage === pageNum ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => setMeetingPage(pageNum)}>{pageNum}</button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${meetingPage === getMeetingTotalPages() ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setMeetingPage(p => Math.min(getMeetingTotalPages(), p + 1))} disabled={meetingPage === getMeetingTotalPages()}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Voice Tab Content */}
          {activeTab === "Voice" && (
            <VoiceTranscriptPanel
              isActive={isVoiceActive}
              conversationHistory={voiceConversationHistory}
              sessionDuration={voiceSessionDuration}
              userId={userId}
            />
          )}
        </div>
      </div>
      <ReplyModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          // window.location.reload()
        }}
        notification={selectedNotification}
        // replyMessage={replyMessage}
        // setReplyMessage={setReplyMessage}
        handleSendReply={() => handleSendReply(selectedNotification.id)} // Pass notification ID to handleSendReply
        disableSendButton={
          replyMessage.trim() === "" ||
          (selectedNotification?.user_query &&
            selectedNotification?.query_response)
        } // Disable send button if reply message is blank or user_query and query_response exist
        workflowUrl={workflowUrl} // Pass workflow URL to ReplyModal
        onNotificationUpdate={onNotificationUpdate} // Pass the callback
        alreadyReplied={alreadyReplied}
        onAlreadyRepliedChange={() => {
          setAlreadyReplied(!alreadyReplied);
        }}
        userId={userId}
      />
      <MeetingModal
        show={showMeetingModal}
        onHide={() => {
          setShowMeetingModal(false);
        }}
        meeting_summaries_id={selectedMeeting}
        userId={userId}
      />
      {showFormPopup && selectedNotification && (
        <SessionPopup
          show={showFormPopup}
          handleClose={() => setShowFormPopup(false)}
          title={"New Topic"}
          button={"Create"}
          handleConfirm={createNotificationSession}
          initialValue={selectedNotification?.title || ""}
        />
      )}
      <QueueSessionPopup
        show={showQueueFormPopup}
        handleClose={() => setShowQueueFormPopup(false)}
        title={"New Topic"}
        button={"Create"}
        sideConvoList={sideConvoList}
        apiUrl={zoomey_api_url}
        queueId={queueSelectId}
        loginUserID={userId}
        apiToken={apiToken}
        isStandalone={isQueueStandalone}
      />
      <MeetingCreatesessionmodal
        show={showMeetingSessionModal}
        handleClose={() => setShowMeetingSessionModal(false)}
        apiUrl={zoomey_api_url}
        sideConvoList={sideConvoList}
        meetingId={meetingSessionMeetingId}
        meeting_summaries_id={selectedMeeting}
        loginUserID={userId}
        apiToken={apiToken}
      />
      <ConfirmationPopup
        show={showConfirmationPopup}
        handleClose={() => setShowConfirmationPopup(false)}
        title="Clear All Notifications"
        body="Are you sure you want to clear all notifications?"
        handleConfirm={() => {
          clearAllNotifications();
          setShowConfirmationPopup(false);
          clearNotifications([]);
        }}
      />
      <ConfirmationPopup
        show={showQueueDeletePopup}
        handleClose={() => setShowQueueDeletePopup(false)}
        title="Delete Queue Request"
        body="Are you sure you want to delete this queue request?"
        handleConfirm={() => {
          if (queueView) {
            handleDeleteQueue(queueView);
          }
          setShowQueueDeletePopup(false);
          setShowQueueModal(false);
          setSelectedQueueRequest(null);
        }}
      />
      <ConfirmationPopup
        show={showQueueConfirmationPopup}
        handleClose={() => setShowQueueConfirmationPopup(false)}
        title="Clear All Queue Notifications"
        body="Are you sure you want to clear all queue request?"
        handleConfirm={() => {
          clearAllQueueRequest();
          setShowQueueConfirmationPopup(false);
        }}
      />
      <ConfirmationPopup
        show={showMeetingConfirmationPopup}
        handleClose={() => setShowMeetingConfirmationPopup(false)}
        title="Clear All Meetings"
        body="Are you sure you want to clear all meetings?"
        handleConfirm={() => {
          handleClearAllMeetings();
          setShowMeetingConfirmationPopup(false);
        }}
      />
      <Modal
        className="max_width_modal"
        show={showQueueModal}
        size={"xl"}
        onHide={() => {
          setShowQueueModal(false);
          setSelectedQueueRequest(null);
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton style={{ overflow: "hidden", flexWrap: "nowrap" }}>
          <div className="d-flex justify-content-between align-items-center w-100" style={{ overflow: "hidden", minWidth: 0 }}>
            <Modal.Title style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: "1 1 0",
              minWidth: 0,
            }}> {queueViewTitle || "No Title"}</Modal.Title>
            <div style={{ flexShrink: 0, marginLeft: "12px" }}>
              <Button
                className="queue-create-btn"
                onClick={() => {
                  setShowQueueModal(false);
                  setSelectedQueueRequest(null);
                  const req = queueRequests.find(r => r.id === queueView);
                  setIsQueueStandalone(!req?.session_id);
                  setShowQueueFormPopup(true);
                  setQueueSelectId(queueView);
                }}
              >
                {" "}
                Create topic{" "}
              </Button>
              <button
                className="btn btn-danger"
                style={{
                  marginLeft: "8px",
                  backgroundColor: "var(--danger)",
                  borderColor: "var(--danger)",
                }}
                onClick={() => {
                  setShowQueueDeletePopup(true);
                  setShowQueueModal(false);
                }}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body
          className="custom-scrollbar"
          ref={modalBodyRef}
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* {isLoading && (
            <>
            <div style={{ 
              position: "absolute", 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: "rgba(255,255,255,0.8)", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              zIndex: 10 
            }}>
              <div style={{ textAlign: "center" }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  Processing audio...
                </div>
              </div>
            </div>
            </>
          )} */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            {selectedQueueRequest && selectedQueueRequest.length > 0 ? (
              selectedQueueRequest.map((request, index) => (
                <React.Fragment key={request.id || index}>
                  {/* User message (right) */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "12px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="chat-user-msg"
                      style={{
                        background: request.isAudioOnly
                          ? "transparent"
                          : "var(--teal-bg)",
                        color: "var(--textwhite)",
                        padding: request.isAudioOnly ? "0" : "12px 16px",
                        borderRadius: "12px",
                        maxWidth: "70%",
                        wordBreak: "break-all",
                        overflowWrap: "break-word",
                      }}
                    >
                      <div
                        style={{
                          paddingRight: request.isAudioOnly ? "0" : "0px",
                        }}
                      >
                        {/* Show processing state for audio */}
                        {request.isProcessing ? (
                          //  (
                          //   <div
                          //     style={{
                          //       display: "flex",
                          //       alignItems: "center",
                          //       gap: "12px",
                          //       padding: "8px 12px",
                          //       background: "transparent",
                          //       borderRadius: "12px",
                          //     }}
                          //   >
                          //     <div
                          //       style={{
                          //         display: "flex",
                          //         alignItems: "center",
                          //         gap: "8px",
                          //       }}
                          //     >
                          //       <i
                          //         className="bi bi-mic-fill"
                          //         style={{ fontSize: "14px", opacity: 0.7 }}
                          //       ></i>
                          //       <span style={{ fontSize: "12px", opacity: 0.7 }}>
                          //         Processing voice message
                          //       </span>
                          //     </div>
                          //     <div
                          //       className="audio-processing-spinner"
                          //       style={{
                          //         width: "16px",
                          //         height: "16px",
                          //         border: "2px solid rgba(255,255,255,0.3)",
                          //         borderTop: "2px solid white",
                          //         borderRadius: "50%",
                          //         animation: "spin 1s linear infinite",
                          //       }}
                          //     ></div>
                          //   </div>
                          // )
                          <span>Processing...</span>
                        ) : request.isAudioOnly ? (
                          /* Audio-only bubble - exactly like ChatBot */
                          <audio
                            controls
                            src={request.audioUrl}
                            style={{
                              maxWidth: "100%",
                              width: "250px", // Fixed width like ChatBot
                            }}
                          />
                        ) : request.isFileMessage ? (
                          /* File message display */
                          <div>
                            {request.fileType &&
                            request.fileType.startsWith("image/") ? (
                              /* Image message - show larger image */
                              <div>
                                <img
                                  src={request.fileUrl}
                                  alt={request.fileName}
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() =>
                                    openFileModal(
                                      request.fileUrl,
                                      "image",
                                      request.fileName
                                    )
                                  }
                                  onError={(e) => {
                                    // Fallback to smaller thumbnail if main image fails
                                    e.target.style.maxWidth = "60px";
                                    e.target.style.maxHeight = "60px";
                                  }}
                                />
                                {/* Show text below image if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {request.user_query}
                                    </div>
                                  )}
                              </div>
                            ) : (
                              /* Non-image file message - show with icon */
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    background: "rgba(255,255,255,0.1)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() => {
                                    // Check if it's a PDF or other file type
                                    if (
                                      request.fileUrl.includes(".pdf") ||
                                      request.fileName
                                        .toLowerCase()
                                        .includes(".pdf")
                                    ) {
                                      openFileModal(
                                        request.fileUrl,
                                        "pdf",
                                        request.fileName
                                      );
                                    } else {
                                      // For other file types, try to open in modal or fallback to new tab
                                      openFileModal(
                                        request.fileUrl,
                                        "other",
                                        request.fileName
                                      );
                                    }
                                  }}
                                >
                                  {getFileIcon(request.fileName, 24)}
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {request.fileName}
                                    </div>
                                  </div>
                                </div>
                                {/* Show text below file if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {request.user_query}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Text-only bubble */
                          <div style={{ display: "flex", alignItems: "start" }}>
                            <span style={{ flex: 1, paddingRight: "12px" }}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: formatResponseMessage(
                                    formatMentionSpacing(
                                      request.user_query ||
                                        "User message text goes here."
                                    )
                                  ),
                                }}
                              />
                            </span>
                            {/* Copy and Edit icons for user text message */}
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "8px",
                                gap: "8px",
                              }}
                            >
                              {/* Copy icon */}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                onClick={() => {
                                  let textToCopy = request.user_query;
                                  const selectedText = window
                                    .getSelection()
                                    .toString();
                                  if (selectedText) {
                                    textToCopy = selectedText;
                                  }
                                  navigator.clipboard.writeText(textToCopy);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Copy message"
                              >
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                              </svg>
                              {/* Pencil (edit) icon */}
                              <i
                                className="bi bi-pencil-fill"
                                style={{ fontSize: "12px", cursor: "pointer" }}
                                title="Edit message"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReplyMessage(request.user_query);
                                  if (textareaRef.current) {
                                    textareaRef.current.focus();
                                  }
                                }}
                              ></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bot message (left) - Only show for text messages, not audio-only */}
                  {!request.isAudioOnly && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        marginBottom: "12px",
                        width: "100%",
                        position: "relative", // for dropdown positioning
                      }}
                    >
                      <div
                        style={{
                          // background: "#F4F4F5",
                          backgroundColor: "transparent",
                          color: "var(--text)",
                          padding: "12px 16px 12px 45px",
                          borderRadius: "12px",
                          maxWidth: "100%",
                          wordBreak: "break-all",
                          overflowWrap: "break-word",
                        }}
                      >
                        {/* 3-dot icon */}
                        <div style={{ position: "absolute", top: 8, right: 8 }}>
                          <div className="dropdown">
                            <i
                              className="bi bi-three-dots-vertical"
                              style={{ cursor: "pointer", fontSize: "1.2em" }}
                              id={`dropdownMenuButton-${index}`}
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            ></i>
                            <ul
                              className="dropdown-menu"
                              aria-labelledby={`dropdownMenuButton-${index}`}
                            >
                              <li
                                className="dropdown-item"
                                onClick={() => {
                                  setShowAddToSessionModal(true);
                                  setAddToChatText(request.response || "");
                                }}
                              >
                                <i className="bi bi-plus-square-dotted"></i>
                                &nbsp; Add to Topic
                              </li>
                              {/* Future: Add more options here */}
                            </ul>
                          </div>
                        </div>
                        <span
                          className="zoomey-title"
                          style={{ fontWeight: "bold", marginLeft: "-47px" }}
                        >
                          <span className="chatbot_z_logo">Z</span>
                          Zoomey
                        </span>
                        <div
                          style={{ paddingRight: "40px" }}
                          dangerouslySetInnerHTML={{
                            __html: formatResponseMessage(
                              request?.response ||
                                (() => {
                                  // Check if this is the last message
                                  const isLastMessage = index === selectedQueueRequest.length - 1;
                                  // Check if request is older than 1 minute
                                  const requestTime = moment.utc(request.createdAt);
                                  const now = moment();
                                  const isOlderThanOneMinute = now.diff(requestTime, 'minutes') >= 1;

                                  if (isLastMessage && !isOlderThanOneMinute) {
                                    return "Thinking...";
                                  }
                                  return "You stopped this request";
                                })()
                            ),
                          }}
                        />
                        {/* <br /> */}
                        <span>
                          {getTimeZone(request.created_at)}
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <p className="mb-0">No queue requests found</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {/* File Preview Section - Exact same as ReplyModal */}
          {selectedFiles.length > 0 && (
            <div
              style={{
                maxHeight: "120px",
                overflowY: "auto",
                padding: "8px",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                background: "#f8f9fa",
                width: "100%",
                marginBottom: "8px",
              }}
            >
              {filePreviews.map((preview, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    background: "#fff",
                    borderRadius: "8px",
                    margin: "4px 0",
                    border: "1px solid #dee2e6",
                  }}
                >
                  {preview.type === "image" ? (
                    <>
                      <img
                        src={preview.url}
                        alt={preview.name}
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "2px solid #e0e0e0",
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          marginLeft: "12px",
                          fontSize: "14px",
                          color: "#495057",
                        }}
                      >
                        {preview.name}
                      </span>
                    </>
                  ) : (
                    <>
                      {getFileIcon(preview.name, 28)}
                      <span
                        style={{
                          flex: 1,
                          marginLeft: "12px",
                          fontSize: "14px",
                          color: "#495057",
                        }}
                      >
                        {preview.name}
                      </span>
                    </>
                  )}
                  <button
                    onClick={() => removeFile(index)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#dc3545",
                      cursor: "pointer",
                      fontSize: "18px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                    title="Remove file"
                    onMouseEnter={(e) =>
                      (e.target.style.background = "#f8f9fa")
                    }
                    onMouseLeave={(e) => (e.target.style.background = "none")}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input Section */}
          {isAudioProcessing && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px 12px",
                // background: "var(--background)",
                borderRadius: "8px",
                marginBottom: "8px",
              }}
              className="w-100"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "3px solid var(--teal-bg)",
                    borderRadius: "50%",
                    animation: "spin .5s linear infinite",
                  }}
                ></div>
                {/* Mic Icon */}
                <i
                  className="bi bi-mic-fill"
                  style={{
                    fontSize: "14px",
                    opacity: 0.7,
                    color: "var(--text)",
                  }}
                ></i>
                <span
                  style={{
                    fontSize: "12px",
                    opacity: 0.7,
                    color: "var(--text)",
                  }}
                >
                  Processing your voice...
                </span>
              </div>
              <style>
                {`
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}
              </style>
            </div>
          )}
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <ModeSelector onModeChange={setSelectedMode} />
            <MentionsInput
              ref={mentionsInputRef}
              inputText={replyMessage}
              setInputText={async (text) => {
                setReplyMessage(text);
                // Save draft for queue request
                if (queueView && userId) {
                  const key = `queue_${queueView}_${userId}`;
                  await saveDraft(key, text);
                }
              }}
              textareaRef={textareaRef}
              onPasteFile={(file, preview) => {
                setSelectedFiles([file]);
                setFilePreviews([preview]);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (replyMessage.trim() !== "" || selectedFiles.length > 0) {
                    if (selectedFiles.length > 0) {
                      handleSendWithFiles();
                    } else {
                      const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                      mentionsInputRef.current?.clearMention();
                      sendChatMessage(q);
                    }
                  }
                }
              }}
              placeholder="What would you like me to handle from this queue?"
              // className="form-control"
              style={{ resize: "none", flex: 1 }}
              rows={4}
            />
            {!audioRecording && (
              <>
                <button
                  onClick={() => {
                    if (selectedFiles.length > 0) {
                      handleSendWithFiles();
                    } else {
                      const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                      mentionsInputRef.current?.clearMention();
                      sendChatMessage(q);
                    }
                  }}
                  disabled={
                    replyMessage.trim() === "" && selectedFiles.length === 0
                  }
                  style={{
                    border: "none",
                    background: "var(--teal-bg)",
                    padding: "8px",
                    borderRadius: "8px",
                    width: "40px",
                    height: "40px",
                  }}
                >
                  <i className="bi bi-send"></i>
                </button>

                {/* File upload button - circular like ReplyModal */}
                <input
                  type="file"
                  id="file-upload-queue"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept="image/*,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt"
                />
                <button
                  onClick={() =>
                    document.getElementById("file-upload-queue")?.click()
                  }
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "transparent",
                    color: "var(--text)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    opacity: isLoading || isSending || isStreaming ? 0.5 : 1,
                  }}
                  title="Attach file"
                  disabled={isLoading || isSending || isStreaming}
                >
                  <i
                    className="bi bi-paperclip"
                    style={{ fontSize: "20px", cursor: "pointer" }}
                  ></i>
                </button>
              </>
            )}

            {/* Add Record Component for Queue Modal */}
            <Record
              onStop={handleQueueAudioRecorded}
              setAudioInput={setAudioInput}
              setAudioRecording={setAudioRecording}
              isLoading={isLoading || isSending || isStreaming}
              setIsUserQueyLoading={setIsUserQueyLoading}
              isDarkModeInternal={isDarkModeInternal}
              setIsAudioProcessing={setIsAudioProcessing}
              setIsVoiceProcessing={setIsAudioProcessing} // <-- ADD THIS LINE
              setCopyToTextArea={setCopyToTextArea}
              setCancelAudio={setCancelAudio}
            />
          </div>
        </Modal.Footer>
      </Modal>

      <AddToSessionModal
        show={showAddToSessionModal}
        handleClose={() => setShowAddToSessionModal(false)}
        activeSessionId={queueView}
        addToChatText={addToChatText}
        userId={userId}
      />

      {/* ImageviewModal for file preview */}
      <ImageviewModal
        show={showFileModal}
        onHide={() => setShowFileModal(false)}
        fileUrl={selectedFileUrl}
        fileType={selectedFileType}
        fileName={selectedFileUrl ? selectedFileUrl.split("/").pop() : ""}
      />
    </>
  );
};

export default RightSidebar;
